import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * WaCalls Phone Widget - Softphone completo
 * Discador flutuante com captura de microfone e áudio, usando a API WaCalls.
 * Funciona como telefone: digita número, liga, fala e ouve pelo computador.
 */
const WaCallsPhoneWidget = ({
  token,
  baseUrl = 'https://voip.loginzap.com.br',
  sessionId,
  position = 'bottom-left',
  name = 'VoIP Phone',
  initialMinimized = true,
  onCallStart,
  onCallEnd,
  onError,
  initialNumber = '',
  themeColor = '#16a34a'
}) => {
  const [isMinimized, setIsMinimized] = useState(initialMinimized);
  const [isConnected, setIsConnected] = useState(false);
  const [isInCall, setIsInCall] = useState(false);
  const [currentNumber, setCurrentNumber] = useState(initialNumber);
  const [callStatus, setCallStatus] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const [incomingCall, setIncomingCall] = useState(null);
  const [callStartTime, setCallStartTime] = useState(null);
  const [numberError, setNumberError] = useState('');
  const [callerName, setCallerName] = useState('');
  const [activeCallId, setActiveCallId] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [micPermission, setMicPermission] = useState(false);

  const eventSourceRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const callingSoundRef = useRef(null);
  const ringingSoundRef = useRef(null);

  // ===== API Helper =====
  const apiCall = useCallback(async (method, path, body) => {
    const url = `${baseUrl}${path}`;
    const opts = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    };
    if (body) opts.body = JSON.stringify(body);
    const res = await fetch(url, opts);
    if (res.status === 204) return null;
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Erro ${res.status}`);
    }
    return res.json();
  }, [baseUrl, token]);

  // ===== Microfone - Capturar áudio do computador =====
  const requestMicrophone = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      setMicPermission(true);
      console.log('[WaCalls] Microfone capturado com sucesso');
      return stream;
    } catch (err) {
      console.error('[WaCalls] Erro ao capturar microfone:', err);
      setMicPermission(false);
      if (onError) onError(new Error('Permissão de microfone negada'));
      return null;
    }
  }, [onError]);

  const releaseMicrophone = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  }, []);

  // ===== Sons de chamada =====
  const playCalling = useCallback(() => {
    stopCalling();
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 440;
      gain.gain.value = 0.1;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      callingSoundRef.current = { ctx, osc, gain };
      // Beep intermitente
      const interval = setInterval(() => {
        if (gain.gain.value > 0) {
          gain.gain.value = 0;
        } else {
          gain.gain.value = 0.1;
        }
      }, 1000);
      callingSoundRef.current.interval = interval;
    } catch (e) {
      console.warn('[WaCalls] Erro ao tocar som de chamada:', e);
    }
  }, []);

  const stopCalling = useCallback(() => {
    if (callingSoundRef.current) {
      try {
        clearInterval(callingSoundRef.current.interval);
        callingSoundRef.current.osc.stop();
        callingSoundRef.current.ctx.close();
      } catch (_) {}
      callingSoundRef.current = null;
    }
  }, []);

  const playRinging = useCallback(() => {
    stopRinging();
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 520;
      gain.gain.value = 0.15;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      ringingSoundRef.current = { ctx, osc, gain };
      const interval = setInterval(() => {
        if (gain.gain.value > 0) {
          gain.gain.value = 0;
        } else {
          gain.gain.value = 0.15;
        }
      }, 600);
      ringingSoundRef.current.interval = interval;
    } catch (e) {
      console.warn('[WaCalls] Erro ao tocar ring:', e);
    }
  }, []);

  const stopRinging = useCallback(() => {
    if (ringingSoundRef.current) {
      try {
        clearInterval(ringingSoundRef.current.interval);
        ringingSoundRef.current.osc.stop();
        ringingSoundRef.current.ctx.close();
      } catch (_) {}
      ringingSoundRef.current = null;
    }
  }, []);

  // ===== Timer de duração =====
  const startDurationTimer = useCallback(() => {
    stopDurationTimer();
    setCallDuration(0);
    const start = Date.now();
    setCallStartTime(start);
    const interval = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    durationIntervalRef.current = interval;
  }, []);

  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    setCallDuration(0);
    setCallStartTime(null);
  }, []);

  const formatDuration = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  // ===== SSE - Eventos em tempo real =====
  useEffect(() => {
    if (!token || !baseUrl) return;

    const clientId = `wacalls-phone-${Date.now()}`;
    const url = `${baseUrl}/api/events?token=${token}&clientId=${clientId}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onopen = () => setIsConnected(true);
    es.onerror = () => setIsConnected(false);

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleSSE(data);
      } catch (e) {}
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
  }, [token, baseUrl]);

  const handleSSE = useCallback((data) => {
    switch (data.type) {
      case 'incoming':
        setIncomingCall({
          sessionId: data.sessionId,
          id: data.id,
          peer: data.peer,
          phone: data.phone || null,
        });
        // Usar data.phone se disponível (número real com DDI), senão limpar o peer
        const incomingPhone = data.phone || (data.peer || '').replace(/@.*$/, '') || 'Desconhecido';
        setCallerName(incomingPhone);
        playRinging();
        break;

      case 'incoming-claimed':
        stopRinging();
        setIncomingCall(null);
        break;

      case 'call-status':
        if (data.id) setActiveCallId(data.id);
        if (data.status === 'starting') {
          setCallStatus('Iniciando...');
        } else if (data.status === 'ringing') {
          setCallStatus('Chamando...');
        } else if (data.status === 'connected') {
          setCallStatus('Em chamada');
          stopCalling();
          stopRinging();
          startDurationTimer();
        }
        break;

      case 'call-ended':
        setIsInCall(false);
        setCallStatus('');
        setCallerName('');
        setCurrentNumber('');
        setActiveCallId(null);
        setIncomingCall(null);
        setIsMuted(false);
        stopDurationTimer();
        stopCalling();
        stopRinging();
        if (onCallEnd) onCallEnd(data);
        break;

      default:
        break;
    }
  }, [onCallEnd, playCalling, stopCalling, playRinging, stopRinging, startDurationTimer, stopDurationTimer, releaseMicrophone]);

  // ===== Fazer chamada =====
  const makeCall = useCallback(async () => {
    if (!currentNumber || isInCall) return;

    const digits = currentNumber.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 13) {
      setNumberError('Número inválido (10-13 dígitos)');
      return;
    }

    if (!sessionId) {
      setNumberError('Sessão não configurada');
      return;
    }

    try {
      setIsInCall(true);
      setCallStatus('Iniciando...');
      setCallerName(digits);
      setNumberError('');
      playCalling();

      // Abrir softphone popup com áudio WebRTC
      const params = new URLSearchParams({
        token,
        phone: digits,
        sessionId,
        record: 'true',
        start_if_ready: 'true',
        close_after_call: 'true',
      });

      const callUrl = `${baseUrl}/call?${params.toString()}`;
      window.open(
        callUrl,
        'wacalls-softphone',
        'toolbar=no,scrollbars=no,resizable=yes,top=200,left=200,width=500,height=600'
      );

      if (onCallStart) onCallStart({ phone: digits });
    } catch (error) {
      setIsInCall(false);
      setCallStatus('');
      setCallerName('');
      stopCalling();
      setNumberError(error.message || 'Erro ao ligar');
      if (onError) onError(error);
    }
  }, [currentNumber, isInCall, sessionId, token, baseUrl, playCalling, stopCalling, onCallStart, onError]);

  // ===== Encerrar chamada =====
  const endCall = useCallback(async () => {
    try {
      if (activeCallId && sessionId) {
        await apiCall('DELETE', `/api/sessions/${sessionId}/calls/${activeCallId}`);
      } else {
        // Se não tem callId, buscar chamadas ativas e encerrar
        const res = await fetch(`${baseUrl}/api/sessions/${sessionId}/history`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const active = (data.rows || []).find(c => c.status !== 'ended');
          if (active) {
            await apiCall('DELETE', `/api/sessions/${sessionId}/calls/${active.callId}`);
          }
        }
      }
    } catch (e) {
      console.error('[VoIP] Erro ao encerrar:', e);
    }
    // Forçar reset do estado local
    setIsInCall(false);
    setCallStatus('');
    setCallerName('');
    setActiveCallId(null);
    stopDurationTimer();
    stopCalling();
  }, [activeCallId, sessionId, token, baseUrl, apiCall, stopDurationTimer, stopCalling]);

  // ===== Aceitar chamada recebida =====
  const answerCall = useCallback(async () => {
    if (!incomingCall) return;
    try {
      stopRinging();

      // Obter o número real do chamador (usar phone se disponível, senão limpar peer)
      const cleanPhone = incomingCall.phone || (incomingCall.peer || '').replace(/@.*$/, '');

      if (!cleanPhone || cleanPhone === 'Desconhecido') {
        // Sem número válido, aceitar via API direto (sem áudio WebRTC)
        await apiCall('POST', `/api/sessions/${incomingCall.sessionId}/calls/${incomingCall.id}/accept`);
        setActiveCallId(incomingCall.id);
        setCallerName(cleanPhone || 'Desconhecido');
        setIsInCall(true);
        setCallStatus('Em chamada');
        startDurationTimer();
        setIncomingCall(null);
        setIsMinimized(false);
        if (onCallStart) onCallStart({ callId: incomingCall.id, peer: incomingCall.peer });
        return;
      }

      // Abrir o popup /call com o número do chamador — o popup gerencia WebRTC + accept
      const params = new URLSearchParams({
        token,
        phone: cleanPhone,
        sessionId: incomingCall.sessionId || sessionId,
        callId: incomingCall.id,
        mode: 'answer',
        name: callerName || cleanPhone,
        close_after_call: 'true',
      });

      const callUrl = `${baseUrl}/call?${params.toString()}`;
      window.open(
        callUrl,
        'wacalls-softphone',
        'toolbar=no,scrollbars=no,resizable=yes,top=200,left=200,width=500,height=600'
      );

      setActiveCallId(incomingCall.id);
      setCallerName(cleanPhone);
      setIsInCall(true);
      setCallStatus('Em chamada');
      startDurationTimer();
      setIncomingCall(null);

      if (onCallStart) onCallStart({ callId: incomingCall.id, peer: incomingCall.peer });
    } catch (error) {
      if (onError) onError(error);
    }
  }, [incomingCall, token, baseUrl, sessionId, callerName, apiCall, startDurationTimer, stopRinging, onCallStart, onError]);

  // ===== Rejeitar chamada =====
  const rejectCall = useCallback(async () => {
    if (!incomingCall) return;
    try {
      stopRinging();
      await apiCall('POST', `/api/sessions/${incomingCall.sessionId}/calls/${incomingCall.id}/reject`);
      setIncomingCall(null);
      setCallerName('');
    } catch (error) {
      if (onError) onError(error);
    }
  }, [incomingCall, apiCall, stopRinging, onError]);

  // ===== Teclado numérico =====
  const handleKeyPress = (key) => {
    if (isInCall) return;
    if (currentNumber.replace(/\D/g, '').length >= 13) return;
    setCurrentNumber(prev => prev + key);
    setNumberError('');
  };

  const clearNumber = () => {
    setCurrentNumber(prev => prev.slice(0, -1));
    setNumberError('');
  };

  const clearAll = () => {
    setCurrentNumber('');
    setNumberError('');
  };

  // Atualizar número quando initialNumber mudar
  useEffect(() => {
    if (initialNumber && !isInCall) {
      setCurrentNumber(initialNumber);
    }
  }, [initialNumber, isInCall]);

  // Keyboard listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isMinimized || isInCall) return;
      if (/^[0-9*#]$/.test(e.key)) {
        e.preventDefault();
        handleKeyPress(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        clearNumber();
      } else if (e.key === 'Enter' && currentNumber && isConnected) {
        e.preventDefault();
        makeCall();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMinimized, isInCall, currentNumber, isConnected, makeCall]);

  // Cleanup
  useEffect(() => {
    return () => {
      stopDurationTimer();
      stopCalling();
      stopRinging();
      if (eventSourceRef.current) eventSourceRef.current.close();
    };
  }, []);

  const keypadRows = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['*', '0', '#'],
  ];

  // ===== RENDER: Minimizado =====
  if (isMinimized) {
    return (
      <>
        <div style={styles.dock}>
          <style>{`
            @keyframes wacallsPulse {
              0%, 100% { transform: scale(1); box-shadow: 0 4px 12px rgba(22,163,74,0.3); }
              50% { transform: scale(1.1); box-shadow: 0 4px 20px rgba(22,163,74,0.6); }
            }
            @keyframes wacallsIncomingSlideIn {
              from { transform: translateY(80px); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
            @keyframes wacallsIncomingPulse {
              0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(16,185,129,0.5); }
              50% { transform: scale(1.08); box-shadow: 0 0 0 14px rgba(16,185,129,0); }
            }
            @keyframes wacallsIncomingRing {
              0%, 100% { transform: rotate(0deg); }
              25% { transform: rotate(15deg); }
              75% { transform: rotate(-15deg); }
            }
          `}</style>
          <button
            style={{
              ...styles.dockBtn,
              background: isInCall ? 'linear-gradient(135deg, #ef4444, #dc2626)' : themeColor,
              ...(isInCall ? { animation: 'wacallsPulse 1.5s infinite' } : {}),
              ...(incomingCall ? { animation: 'wacallsPulse 0.8s infinite', background: 'linear-gradient(135deg, #10b981, #059669)' } : {}),
            }}
            onClick={() => setIsMinimized(false)}
            title="Abrir discador VoIP"
          >
            📞
          </button>
        </div>

        {/* Notificação global de chamada recebida - aparece mesmo minimizado */}
        {incomingCall && (
          <div style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 99999,
            background: '#1e1e2e',
            borderRadius: '16px',
            padding: '20px 24px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            minWidth: '280px',
            animation: 'wacallsIncomingSlideIn 0.3s ease',
          }}>
            <div style={{
              width: '60px', height: '60px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '26px', animation: 'wacallsIncomingPulse 1.2s infinite',
            }}>
              <span style={{ animation: 'wacallsIncomingRing 0.5s infinite' }}>📞</span>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#a0aec0', fontSize: '12px', marginBottom: '4px' }}>
                📞 Chamada entrante
              </div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '18px' }}>
                {callerName || 'Desconhecido'}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '16px' }}>
              <button
                onClick={rejectCall}
                title="Rejeitar"
                style={{
                  width: '52px', height: '52px', borderRadius: '50%',
                  background: '#ef4444', border: 'none', cursor: 'pointer',
                  fontSize: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#dc2626'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#ef4444'; }}
              >
                📵
              </button>
              <button
                onClick={answerCall}
                title="Atender"
                style={{
                  width: '52px', height: '52px', borderRadius: '50%',
                  background: '#10b981', border: 'none', cursor: 'pointer',
                  fontSize: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#059669'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#10b981'; }}
              >
                📞
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  // ===== RENDER: Expandido =====
  return (
    <div style={styles.widgetRight}>
      <style>{`
        @keyframes wacallsPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes wacallsRing {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(15deg); }
          75% { transform: rotate(-15deg); }
        }
      `}</style>

      <div style={styles.expanded}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={{ fontSize: '14px' }}>{isConnected ? '🟢' : '🔴'}</span>
            <span style={styles.headerTitle}>{name}</span>
          </div>
          <div style={styles.headerRight}>
            <button style={styles.minimizeBtn} onClick={() => setIsMinimized(true)}>−</button>
          </div>
        </div>

        {/* Display */}
        <div style={styles.display}>
          {isInCall ? (
            <div style={styles.callInfoDisplay}>
              <div style={styles.callStatusBadge}>
                {callStatus === 'Em chamada' ? '🟢' : '📞'} {callStatus}
              </div>
              <div style={styles.callerNumber}>{callerName}</div>
              {callDuration > 0 && (
                <div style={styles.durationText}>{formatDuration(callDuration)}</div>
              )}
            </div>
          ) : (
            <div style={styles.idleDisplay}>
              <div style={styles.numberDisplay}>
                {currentNumber || '_ _ _ _ _ _ _ _ _'}
              </div>
              {numberError && <div style={styles.errorText}>{numberError}</div>}
            </div>
          )}
        </div>

        {/* Teclado numérico */}
        {!isInCall && (
          <div style={styles.keypad}>
            {keypadRows.map((row, i) => (
              <div key={i} style={styles.keypadRow}>
                {row.map((key) => (
                  <button
                    key={key}
                    style={styles.keypadKey}
                    onClick={() => handleKeyPress(key)}
                    onMouseDown={(e) => { e.currentTarget.style.background = '#3d3d5c'; }}
                    onMouseUp={(e) => { e.currentTarget.style.background = '#2d2d3d'; }}
                  >
                    {key}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Botões de ação */}
        <div style={styles.actions}>
          {!isInCall ? (
            <>
              <button
                style={{
                  ...styles.actionBtn,
                  ...styles.callBtn,
                  opacity: (!isConnected || !currentNumber) ? 0.5 : 1,
                  cursor: (!isConnected || !currentNumber) ? 'not-allowed' : 'pointer',
                }}
                onClick={makeCall}
                disabled={!isConnected || !currentNumber}
                title="Ligar"
              >
                📞
              </button>
              <button
                style={{ ...styles.actionBtn, ...styles.clearBtn }}
                onClick={clearNumber}
                title="Apagar"
              >
                ⌫
              </button>
              <button
                style={{ ...styles.actionBtn, ...styles.clearAllBtn }}
                onClick={clearAll}
                title="Limpar"
              >
                ✕
              </button>
            </>
          ) : (
            <>
              <button
                style={{
                  ...styles.actionBtn,
                  ...styles.muteBtn,
                  ...(isMuted ? styles.muteBtnActive : {}),
                }}
                onClick={toggleMute}
                title={isMuted ? 'Ativar microfone' : 'Mutar'}
              >
                {isMuted ? '🔇' : '🎤'}
              </button>
              <button
                style={{ ...styles.actionBtn, ...styles.endCallBtn }}
                onClick={endCall}
                title="Encerrar chamada"
              >
                📵
              </button>
            </>
          )}
        </div>

        {/* Chamada recebida */}
        {incomingCall && (
          <div style={styles.incomingOverlay}>
            <div style={styles.incomingContent}>
              <div style={{ fontSize: '36px', animation: 'wacallsRing 0.5s infinite' }}>📞</div>
              <div style={styles.incomingText}>Chamada recebida</div>
              <div style={styles.incomingNumber}>{callerName}</div>
              <div style={styles.incomingActions}>
                <button style={styles.answerBtn} onClick={answerCall} title="Atender">
                  ✅ Atender
                </button>
                <button style={styles.rejectBtn} onClick={rejectCall} title="Rejeitar">
                  ❌ Rejeitar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Áudio gerenciado pelo painel WaCalls (popup) */}
      </div>
    </div>
  );
};

// ===== ESTILOS =====
const styles = {
  dock: {
    position: 'fixed',
    right: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 99998,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    background: 'white',
    borderRadius: '20px',
    padding: '8px 6px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
    border: '1px solid #e8ecf1',
  },
  dockBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    transition: 'all 0.2s ease',
    background: 'transparent',
  },
  widgetRight: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 99998,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  widget: {
    position: 'fixed',
    bottom: '24px',
    left: '24px',
    zIndex: 99998,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  minimizedBtn: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #16a34a, #15803d)',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    boxShadow: '0 4px 16px rgba(22, 163, 74, 0.4)',
    transition: 'all 0.3s ease',
  },
  minimizedBtnActive: {
    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
    boxShadow: '0 4px 16px rgba(239, 68, 68, 0.4)',
    animation: 'wacallsPulse 1.5s infinite',
  },
  expanded: {
    width: '300px',
    background: '#1a1a2e',
    borderRadius: '16px',
    boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
    overflow: 'hidden',
    position: 'relative',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    background: '#16213e',
    borderBottom: '1px solid #2a2a4a',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  headerTitle: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#e0e0e0',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
  },
  minimizeBtn: {
    background: 'none',
    border: 'none',
    color: '#a0a0a0',
    fontSize: '22px',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: '4px',
    lineHeight: 1,
  },
  display: {
    padding: '24px 16px',
    textAlign: 'center',
    minHeight: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  callInfoDisplay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  callStatusBadge: {
    fontSize: '13px',
    color: '#10b981',
    fontWeight: 600,
    background: 'rgba(16, 185, 129, 0.1)',
    padding: '4px 12px',
    borderRadius: '12px',
  },
  callerNumber: {
    fontSize: '20px',
    color: '#ffffff',
    fontWeight: 700,
    fontFamily: 'monospace',
  },
  durationText: {
    fontSize: '28px',
    color: '#ffffff',
    fontWeight: 700,
    fontFamily: 'monospace',
    letterSpacing: '2px',
  },
  idleDisplay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    width: '100%',
  },
  numberDisplay: {
    fontSize: '20px',
    color: '#ffffff',
    fontWeight: 700,
    fontFamily: 'monospace',
    letterSpacing: '2px',
    minHeight: '28px',
  },
  errorText: {
    fontSize: '11px',
    color: '#ef4444',
  },
  keypad: {
    padding: '4px 20px 12px',
  },
  keypadRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  keypadKey: {
    width: '60px',
    height: '48px',
    borderRadius: '12px',
    border: 'none',
    background: '#2d2d3d',
    color: '#ffffff',
    fontSize: '20px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.1s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    padding: '12px 20px 20px',
  },
  actionBtn: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  callBtn: {
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    boxShadow: '0 4px 14px rgba(34, 197, 94, 0.4)',
  },
  endCallBtn: {
    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
    boxShadow: '0 4px 14px rgba(239, 68, 68, 0.4)',
    width: '60px',
    height: '60px',
    fontSize: '24px',
  },
  muteBtn: {
    background: '#2d2d3d',
    border: '2px solid #4a4a6a',
  },
  muteBtnActive: {
    background: '#ef4444',
    border: '2px solid #ef4444',
  },
  clearBtn: {
    background: '#2d2d3d',
    fontSize: '18px',
  },
  clearAllBtn: {
    background: '#2d2d3d',
    fontSize: '14px',
    width: '40px',
    height: '40px',
    alignSelf: 'center',
  },
  incomingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(26, 26, 46, 0.97)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '16px',
    zIndex: 10,
  },
  incomingContent: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '20px',
  },
  incomingText: {
    fontSize: '13px',
    color: '#a0a0a0',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  incomingNumber: {
    fontSize: '20px',
    color: '#ffffff',
    fontWeight: 700,
    fontFamily: 'monospace',
  },
  incomingActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
  },
  answerBtn: {
    padding: '12px 20px',
    borderRadius: '24px',
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 700,
  },
  rejectBtn: {
    padding: '12px 20px',
    borderRadius: '24px',
    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 700,
  },
};

export default WaCallsPhoneWidget;
